// v3.6g — Bulk MYOB Card ID importer.
// Accounts staff exports their MYOB Cards list (Name + Card ID columns),
// pastes it into the textarea here, previews fuzzy-matches against our
// customers, and one-click writes the Card IDs into each customer's
// gl_code so subsequent MYOB Professional Sales exports match on Card ID
// without any manual mapping.
import { useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, AlertTriangle, XCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function BulkCardIdsDialog({ open, onOpenChange, onDone }) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(false);

  // Parse pasted text: accepts TAB or two-or-more-space separators.
  // Rows starting with "#" are treated as comments/skipped.
  const parsed = useMemo(() => {
    if (!raw.trim()) return [];
    const rows = [];
    for (const line of raw.split(/\r?\n/)) {
      const t = line.trim();
      if (!t || t.startsWith("#")) continue;
      // Try tab first, then comma, then 2+ spaces
      let parts = t.split("\t");
      if (parts.length < 2) parts = t.split(",");
      if (parts.length < 2) parts = t.split(/\s{2,}/);
      if (parts.length < 2) continue;
      const name = parts[0].trim();
      // Card ID is usually second column but sometimes last
      const card_id = (parts[1] || "").trim();
      if (name && card_id) rows.push({ name, card_id });
    }
    return rows;
  }, [raw]);

  const runPreview = async () => {
    if (parsed.length === 0) {
      toast.error("No valid rows found. Use one per line: Name<TAB>Card ID");
      return;
    }
    setBusy(true);
    setCommitted(false);
    try {
      const { data } = await api.post("/customers/bulk-card-ids", { mappings: parsed, commit: false });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview || preview.counts.matched === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/customers/bulk-card-ids", { mappings: parsed, commit: true });
      setPreview(data);
      setCommitted(true);
      toast.success(`Applied ${data.counts.updated} Card ID${data.counts.updated === 1 ? "" : "s"}`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setRaw("");
    setPreview(null);
    setCommitted(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="rounded-none max-w-3xl bg-surface border border-industrial p-0" data-testid="bulk-card-ids-dialog">
        <DialogHeader className="p-5 border-b border-industrial">
          <DialogTitle className="heading uppercase text-xl font-black flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary-orange" />
            Bulk Card IDs · MYOB Import
          </DialogTitle>
          <div className="text-xs text-muted-industrial mt-1">
            Paste your MYOB Cards list (Company Name + Card ID) — one customer per line, Tab-separated. We'll fuzzy-match to your GADTECH customers and write Card IDs into each customer's gl_code so MYOB Professional Sales imports match automatically.
          </div>
        </DialogHeader>

        <div className="p-5 space-y-4">
          {!preview && (
            <>
              <div>
                <div className="label-tag mb-2">Paste MYOB Cards</div>
                <textarea
                  data-testid="bulk-card-ids-textarea"
                  value={raw}
                  onChange={(e) => setRaw(e.target.value)}
                  rows={12}
                  placeholder={"CORDWELL BUILDERS\tCORDWE\nMITCHELL SANDS PTY LTD\tMITCHS\nCREATIVE CIVIL 1\tCREATE1\nPROTECT PL\tPROTEC\n# Comments and blank lines are ignored\n"}
                  className="w-full bg-surface-2 border border-industrial rounded-none px-3 py-2 mono text-xs text-ink focus:outline-none focus:border-primary-orange"
                  spellCheck={false}
                />
                <div className="text-[11px] text-muted-industrial mt-1.5 mono">
                  {parsed.length} row{parsed.length === 1 ? "" : "s"} detected · Tab, comma, or 2+ spaces work as separators
                </div>
              </div>
              <div className="text-[11px] text-muted-industrial bg-surface-2 border border-industrial p-3">
                <div className="label-tag mb-1">Tip · Getting MYOB's list</div>
                In MYOB AccountRight → <b>Card File → Cards List</b> → filter to <b>Customer</b> → click the <b>Print/Email → Export to Excel</b> button.
                Open the exported file, select the <b>Name</b> and <b>Card ID</b> columns only, copy, paste here.
              </div>
            </>
          )}

          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                <StatCard label="Input" value={preview.counts.input} tone="muted" />
                <StatCard label="Matched" value={preview.counts.matched} tone="ok" icon={CheckCircle2} />
                <StatCard label="Conflicts" value={preview.counts.conflicts} tone="warn" icon={AlertTriangle} />
                <StatCard label="Unmatched" value={preview.counts.unmatched} tone="err" icon={XCircle} />
              </div>

              {preview.matched.length > 0 && (
                <ResultSection title="Will Update" tone="ok" testId="bulk-cid-matched" rows={
                  preview.matched.map((m) => (
                    <tr key={m.customer_id} className="border-b border-industrial hover:bg-surface-2">
                      <td className="p-2 text-xs">{m.name}</td>
                      <td className="p-2 text-xs mono">→ {m.customer_name}</td>
                      <td className="p-2 text-xs mono text-primary-orange">{m.card_id}</td>
                      <td className="p-2 text-[10px] text-muted-industrial mono uppercase text-right">{m.kind} · {(m.score * 100).toFixed(0)}%</td>
                    </tr>
                  ))
                } />
              )}

              {preview.conflicts.length > 0 && (
                <ResultSection title="Ambiguous · Skipped" tone="warn" testId="bulk-cid-conflicts" rows={
                  preview.conflicts.map((c, i) => (
                    <tr key={i} className="border-b border-industrial">
                      <td className="p-2 text-xs">{c.name}</td>
                      <td className="p-2 text-[11px] text-muted-industrial" colSpan={3}>
                        Matches: {c.candidates.map((k) => `${k.name} (${(k.score * 100).toFixed(0)}%)`).join(" · ")}
                      </td>
                    </tr>
                  ))
                } />
              )}

              {preview.unmatched.length > 0 && (
                <ResultSection title="No Match · Skipped" tone="err" testId="bulk-cid-unmatched" rows={
                  preview.unmatched.map((u, i) => (
                    <tr key={i} className="border-b border-industrial">
                      <td className="p-2 text-xs">{u.name}</td>
                      <td className="p-2 text-xs mono text-muted-industrial">{u.card_id}</td>
                      <td className="p-2 text-[11px] text-muted-industrial" colSpan={2}>no customer close enough</td>
                    </tr>
                  ))
                } />
              )}
            </div>
          )}
        </div>

        <div className="border-t border-industrial p-4 flex items-center justify-end gap-2 bg-surface-2">
          {preview ? (
            <>
              <Button variant="ghost" onClick={reset} data-testid="bulk-cid-back" className="rounded-none mono uppercase text-xs tracking-widest">
                Start Over
              </Button>
              {!committed && (
                <Button
                  onClick={commit}
                  disabled={busy || preview.counts.matched === 0}
                  data-testid="bulk-cid-commit"
                  className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold text-black"
                >
                  {busy ? "Applying…" : `Apply ${preview.counts.matched} Card ID${preview.counts.matched === 1 ? "" : "s"}`}
                </Button>
              )}
              {committed && (
                <Button
                  onClick={() => onOpenChange(false)}
                  data-testid="bulk-cid-close"
                  className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold text-black"
                >
                  Done
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="bulk-cid-cancel" className="rounded-none mono uppercase text-xs tracking-widest">
                Cancel
              </Button>
              <Button
                onClick={runPreview}
                disabled={busy || parsed.length === 0}
                data-testid="bulk-cid-preview"
                className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold text-black"
              >
                {busy ? "Matching…" : `Preview ${parsed.length || 0} Match${parsed.length === 1 ? "" : "es"}`}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, tone, icon: Icon }) {
  const toneStyles = {
    muted: "border-industrial text-muted-industrial",
    ok: "border-emerald-600/40 text-emerald-500",
    warn: "border-amber-500/40 text-amber-500",
    err: "border-rose-600/40 text-rose-500",
  }[tone] || "border-industrial";
  return (
    <div className={`border ${toneStyles} p-3 bg-surface-2`}>
      <div className="flex items-center gap-1.5 label-tag">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="mono text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

function ResultSection({ title, tone, rows, testId }) {
  const bar = { ok: "bg-emerald-500", warn: "bg-amber-500", err: "bg-rose-500" }[tone] || "bg-industrial";
  return (
    <div className="border border-industrial" data-testid={testId}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-industrial bg-surface-2">
        <div className={`w-1.5 h-4 ${bar}`} />
        <div className="label-tag">{title}</div>
        <div className="text-[10px] mono text-muted-industrial ml-auto">{rows.length} row{rows.length === 1 ? "" : "s"}</div>
      </div>
      <div className="max-h-56 overflow-y-auto">
        <table className="w-full">
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );
}
